/**
 * 侧边栏的「有新消息」标识 —— **客户端版，是过渡不是终点**。
 *
 * ------------------------------------------------------------------
 * # 为什么是客户端版
 *
 * 库里没有 last_read 这一列，会话 DTO 里没有 unread 字段：
 * 「未读」这个概念 2026-09-08 之前从来没建过。服务端版要建表、改契约
 * （L-68，流 0），而 arron 现在就看不见新消息。
 *
 * 这一版用两样现成的东西：会话的 `updated_at`（AppendMessage 会推进它），
 * 与浏览器里记的「这条会话我上次看到的 updated_at」。比它新就打点。
 *
 * # 它的边界，写在这儿免得被当成设计
 *
 * - **换设备、清浏览器就丢**。未读是每个人自己的事实，只能落在服务端。
 * - 自己刚发的那条也会推进 updated_at。所以正看着的那条会话**随时标为已看**，
 *   而不是只在点开那一刻标一次。
 * - localStorage 读写都可能抛（隐私窗口、缩略图渲染）。**全部 try/catch，
 *   抛了就当没记过**——一个因为存不了「上次看到」而白屏的侧边栏是本末倒置。
 *
 * L-68 落地那天，这个文件整个删掉，`unread` 从 DTO 读。
 * ------------------------------------------------------------------
 */

const KEY = 'npc.lulu.lastSeen.v1'

type SeenMap = Readonly<Record<string, string>>

function load(): SeenMap {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw === null) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

function save(map: SeenMap): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(map))
  } catch {
    // 存不了就存不了。下次打开这条会话会再标一次。
  }
}

/** 记下「这条会话我看到了 updated_at 这一刻」。 */
export function markSeen(conversationId: string, updatedAt: string): void {
  if (conversationId === '' || updatedAt === '') return
  const map = load()
  if (map[conversationId] === updatedAt) return
  save({ ...map, [conversationId]: updatedAt })
}

/**
 * 算出哪些会话有新东西。
 *
 * 判据：`updated_at` 比上次看到的新，**而且不是正看着的那条**——
 * 正看着的那条永远不算未读，哪怕它刚刷新。
 * 从没看过的会话（本地没记录）**不算未读**：那会让第一次打开时满屏都是点。
 */
export function unreadIds(
  conversations: readonly { readonly id: string; readonly updated_at: string }[],
  activeId: string | undefined,
  seen: SeenMap = load(),
): ReadonlySet<string> {
  const out = new Set<string>()
  for (const c of conversations) {
    if (c.id === activeId) continue
    const last = seen[c.id]
    if (last === undefined) continue
    if (c.updated_at > last) out.add(c.id)
  }
  return out
}

/** 只给用例用：读一份当前的记录。 */
export function loadSeenForTest(): SeenMap {
  return load()
}
